import { createSlice } from "@reduxjs/toolkit";
import { CommissionableServiceResponse } from "../../shared/types";
import { CommissionableServicesThunks } from "../thunks";
import { CommissionCalculationResponse } from "../../shared/types/commission-calculation.type";
import { ParsedError } from "../../services/handle-error.service";

interface State {
  commissionableServices: CommissionableServiceResponse[];
  commissionCalculation?: CommissionCalculationResponse;
  isLoading: boolean;
  // The commission calculation feeds the service/modality/collaborator selectors of the
  // allocation form. Without it the form cannot be filled in, so its loading and error
  // state are tracked apart from the CRUD ones.
  isCalculationLoading: boolean;
  calculationError?: string;
}

const initialState: State = {
  commissionableServices: [],
  commissionCalculation: undefined,
  isLoading: true,
  isCalculationLoading: false,
  calculationError: undefined,
};

export const commissionableServicesSlice = createSlice({
  name: "commissionableServices",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(
      CommissionableServicesThunks.fetchItems.pending,
      (state) => {
        state.isLoading = true;
      }
    );
    builder.addCase(
      CommissionableServicesThunks.fetchItems.fulfilled,
      (state, action) => {
        state.commissionableServices = action.payload;
        state.isLoading = false;
      }
    );
    builder.addCase(
      CommissionableServicesThunks.fetchItems.rejected,
      (state) => {
        state.isLoading = false;
      }
    );
    builder.addCase(
      CommissionableServicesThunks.createItem.fulfilled,
      (state, action) => {
        state.commissionableServices.push(action.payload);
      }
    );
    builder.addCase(
      CommissionableServicesThunks.updateItem.fulfilled,
      (state, action) => {
        const index = state.commissionableServices.findIndex(
          (resource) => resource.id === action.payload.id
        );
        state.commissionableServices[index] = action.payload;
      }
    );
    builder.addCase(
      CommissionableServicesThunks.deleteItem.fulfilled,
      (state, action) => {
        state.commissionableServices = state.commissionableServices.filter(
          (resource) => resource.id !== action.payload
        );
      }
    );
    builder.addCase(
      CommissionableServicesThunks.getCommissionCalculation.pending,
      (state) => {
        state.isCalculationLoading = true;
        state.calculationError = undefined;
      }
    );
    builder.addCase(
      CommissionableServicesThunks.getCommissionCalculation.fulfilled,
      (state, action) => {
        state.commissionCalculation = action.payload;
        state.isCalculationLoading = false;
        state.calculationError = undefined;
      }
    );
    builder.addCase(
      CommissionableServicesThunks.getCommissionCalculation.rejected,
      (state, action) => {
        state.isCalculationLoading = false;
        state.calculationError =
          (action.payload as ParsedError)?.message ||
          "No se pudieron cargar los servicios comisionables.";
      }
    );
  },
});

export default commissionableServicesSlice.reducer;
